import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X, Download, FileText, User, Calendar, Quote, Check } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { useAccess } from '@/shared/rbac'
import { useAnnouncementModalStore } from '../store'
import { useAnnouncementDetail, useAnnouncementStateMutations } from '../hooks/use-announcements'
import { CategoryBadge, SeverityPill, MandatoryPill } from './badges'
import { MarkdownView } from './markdown-view'

/** Mounted once (AppShell). Renders the shared detail modal when an id is set. */
export function AnnouncementDetailModalHost() {
  const { canViewAnnouncements } = useAccess()
  const openId = useAnnouncementModalStore((s) => s.openId)
  if (!canViewAnnouncements || !openId) return null
  // `key={openId}` forces a fresh mount whenever the user switches directly
  // from one announcement to another (A → B without an intermediate close).
  // Without it, React reuses the instance across id changes, the mount-time
  // useEffect never re-runs, and a pending ack-close timer from the previous
  // announcement would fire against the new one.
  return <AnnouncementDetailModal key={openId} id={openId} />
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function AnnouncementDetailModal({ id }: { id: string }) {
  const close = useAnnouncementModalStore((s) => s.close)
  const { data: update, isLoading } = useAnnouncementDetail(id)
  const { markRead, dismiss, acknowledge } = useAnnouncementStateMutations()
  const markedRef = useRef<string | null>(null)
  const ackTimerRef = useRef<number | null>(null)

  const isMandatory = !!update?.mandatory_ack
  const isAcknowledged = !!update?.acknowledged_at
  // Mandatory + unacknowledged items still surface the Acknowledge action in the
  // footer, but the modal is freely dismissable (X / scrim / ESC) — dismissing
  // is "read later", NOT an acknowledgement.
  const locked = isMandatory && !isAcknowledged

  // Auto-mark read on open (once per id) so the unread badge settles. Read is
  // distinct from acknowledged — mandatory items still require the button.
  useEffect(() => {
    if (update && !update.read_at && markedRef.current !== id) {
      markedRef.current = id
      markRead.mutate(id)
    }
  }, [update, id, markRead])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [locked, close])

  // Cancel the pending ack-close timer when this modal instance unmounts. The
  // modal unmounts whenever close() runs (host returns null on openId=null),
  // so any pending timer dies with the instance that scheduled it. Fixes:
  //  - opening a DIFFERENT item during the 300ms delay (old timer would close
  //    the new one),
  //  - closing and REOPENING the same item during the delay (an openId===id
  //    check would still pass on the reopened instance).
  useEffect(() => {
    return () => {
      if (ackTimerRef.current !== null) {
        window.clearTimeout(ackTimerRef.current)
        ackTimerRef.current = null
      }
    }
  }, [])

  const handleAcknowledge = () => {
    acknowledge.mutate(id, {
      onSuccess: () => {
        ackTimerRef.current = window.setTimeout(close, 300)
      },
    })
  }
  const handleDismiss = () => {
    dismiss.mutate(id, { onSuccess: close })
  }

  return createPortal(
    <div
      className="modal-overlay modal-overlay--open"
      onClick={close}
    >
      <div className="modal app-portal-scaled modal--wide" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button className="modal__close-btn" onClick={close} aria-label="Close">
          <X size={15} strokeWidth={2.5} />
        </button>

        {isLoading || !update ? (
          <div className="modal__body">
            <div className="anc-skeleton" style={{ height: 20, width: '40%' }} />
            <div className="anc-skeleton" style={{ height: 14, width: '90%', marginTop: 12 }} />
            <div className="anc-skeleton" style={{ height: 14, width: '80%', marginTop: 8 }} />
          </div>
        ) : (
          <>
            <div className="modal__body">
              <div className="anc-badge-row">
                <CategoryBadge category={update.category} />
                <SeverityPill severity={update.severity} />
                {isMandatory && <MandatoryPill acknowledged={isAcknowledged} />}
              </div>

              <h2 className="anc-modal-title">{update.title}</h2>

              <div className="anc-meta-row">
                <span><User size={12} /> {update.author_name || 'Medicodio'}</span>
                <span><Calendar size={12} /> {fmtDate(update.published_at ?? update.created_at)}</span>
                {update.source_citation && (
                  <span><Quote size={12} /> {update.source_citation}</span>
                )}
              </div>

              {update.body_md && <MarkdownView>{update.body_md}</MarkdownView>}

              {update.affected_codes.length > 0 && (
                <div className="anc-section">
                  <div className="anc-section-label">Affected codes</div>
                  <div className="anc-chips">
                    {update.affected_codes.map((c) => (
                      <span key={c.id} className="anc-chip">{c.code}</span>
                    ))}
                  </div>
                </div>
              )}

              {update.attachments.length > 0 && (
                <div className="anc-section">
                  <div className="anc-section-label">Attachments</div>
                  <div className="anc-attachments">
                    {update.attachments.map((a) => (
                      <a key={a.id} href={a.blob_url} target="_blank" rel="noopener noreferrer" className="anc-attachment">
                        <FileText size={14} />
                        <span className="anc-attachment-name">{a.file_name}</span>
                        <span className="anc-attachment-size">{fmtSize(a.size_bytes)}</span>
                        <Download size={13} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="modal__footer">
              {isMandatory ? (
                <>
                  {!locked && <span className="anc-ack-done"><Check size={13} /> Acknowledged</span>}
                  {locked && (
                    <>
                      <Button variant="secondary" size="sm" onClick={close}>Read later</Button>
                      <Button size="sm" onClick={handleAcknowledge} loading={acknowledge.isPending}>
                        Acknowledge
                      </Button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <Button variant="secondary" size="sm" onClick={handleDismiss} loading={dismiss.isPending}>
                    Dismiss
                  </Button>
                  <Button size="sm" onClick={close}>Done</Button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  )
}
